import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { REPORT_DEPT_QUERY_KEY } from "@shared-domain/unizo/notificationLink";
import { useActiveDepartment } from "@/hooks/useActiveDepartment";
import { useOptionalPreference } from "@/contexts/PreferencesContext";
import {
  deptLinkAction,
  resolveReportDeptView,
  type ReportDeptView,
} from "@/lib/mypage/reportDept";

/** URL で部署を渡すときのクエリ名（通知・メールのリンク・API と同じ。正本は shared）。 */
export const REPORT_DEPT_QUERY = REPORT_DEPT_QUERY_KEY;

/**
 * 日報・管理者ビューを開く部署（PRODUCT_DEV-721 U6）。API には `withDeptQuery(url, deptId)` で渡す。
 * 決め方は `resolveReportDeptView`（URL の部署 → いまの部署）。
 */
export const useReportDept = (): ReportDeptView => {
  const [searchParams] = useSearchParams();
  const { active, all } = useActiveDepartment();
  const queryDeptId = searchParams.get(REPORT_DEPT_QUERY);
  return useMemo(
    () =>
      resolveReportDeptView({
        queryDeptId,
        activeDeptId: active?.deptId ?? null,
        ownDeptIds: all.map((d) => d.deptId),
      }),
    [queryDeptId, active?.deptId, all],
  );
};

/**
 * まとめメール・通知のリンク（`?deptId=`）を開いたら「いまの部署」を切り替える（PRODUCT_DEV-721 U6）。
 * 所属している部署なら切り替えて URL から外す。所属していない部署は URL に残す（`useReportDept` が読むだけで出す）。
 *
 * 画面ごとに書かず、個人設定の Provider の内側に1つだけ置く。
 */
export const ReportDeptLinkSync = (): null => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { active, all, setActive } = useActiveDepartment();
  const queryDeptId = searchParams.get(REPORT_DEPT_QUERY);
  const activeDeptId = active?.deptId ?? null;
  const ownKey = all.map((d) => d.deptId).join(",");
  // PRODUCT_DEV-917: まだ一度も部署を選んでいなければ、リンクの部署を保存する（理由は deptLinkAction）
  const [savedDeptId] = useOptionalPreference("activeDeptId");
  const hasSavedChoice = !!savedDeptId;

  useEffect(() => {
    const { switchTo, strip } = deptLinkAction({
      queryDeptId,
      activeDeptId,
      ownDeptIds: ownKey ? ownKey.split(",") : [],
      hasSavedChoice,
    });
    if (switchTo) setActive(switchTo);
    if (strip) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(REPORT_DEPT_QUERY);
          return next;
        },
        { replace: true },
      );
    }
  }, [queryDeptId, activeDeptId, ownKey, hasSavedChoice, setActive, setSearchParams]);

  return null;
};
